import { useParams, Link } from "react-router-dom";
import { getComicBySlug, comics } from "@/mocks/comics";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import NotFound from "@/pages/NotFound";

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || "");

  if (!comic) return <NotFound />;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${comic.title} - ${comic.author} - 韩国漫画免费在线观看 - 哈哈漫画网`}
        description={`${comic.title}：${comic.description.slice(0, 120)} - 哈哈漫画网提供高清韩漫自由阅读体验。分类：${comic.categoryName}，状态：${comic.status}，评分：${comic.rating}分。`}
        keywords={`${comic.title},${comic.author},${comic.categoryName}漫画,韩国漫画,哈哈漫画网`}
        canonicalUrl={`https://hahacomic.com/comic/${comic.slug}`}
        ogType="article"
        ogImage={comic.cover}
        schemaType="Article"
        articleData={{
          headline: `${comic.title} - ${comic.author}`,
          author: comic.author,
          datePublished: comic.chapters[0]?.updatedAt || "2026-01-01",
          dateModified: comic.updatedAt,
          image: comic.cover,
        }}
      />
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: `${comic.categoryName}漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0 w-full md:w-64">
              <div className="overflow-hidden rounded-lg aspect-[3/4] w-full md:w-64">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 韩国漫画免费在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="text-sm px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                  {comic.categoryName}
                </span>
                <span className={`text-sm px-3 py-1.5 rounded-full font-medium ${
                  comic.status === "已完结" ? "bg-background-200 text-foreground-600" : "bg-accent-100 text-accent-700"
                }`}>
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong className="text-foreground-900">{comic.rating}</strong>
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-4 rounded-xl bg-background-100">
                <div>
                  <span className="text-xs text-foreground-400">作者</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">{comic.author}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-400">总章节</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">{comic.chapters.length} 话</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-400">阅读量</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">
                    {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-foreground-400">最近更新</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">{comic.updatedAt}</p>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-base font-semibold text-foreground-950 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex gap-3">
                <Link
                  to={`/comic/${comic.slug}`}
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始阅读
                </Link>
                <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-bookmark-line mr-1.5"></i>收藏
                </button>
              </div>
            </div>
          </article>

          <section className="mt-10">
            <h2 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
              章节列表
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {comic.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/comic/${comic.slug}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors group cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-mono text-foreground-400 flex-shrink-0">
                      第{chapter.number}话
                    </span>
                    <span className="text-sm text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                      {chapter.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.updatedAt}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {comics
                .filter((c) => c.category === comic.category && c.id !== comic.id)
                .slice(0, 6)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/comic/${related.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="overflow-hidden rounded-lg mb-2 aspect-[3/4]">
                      <img
                        src={related.cover}
                        alt={related.title}
                        title={`${related.title} - ${related.categoryName}韩国漫画免费在线观看`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <p className="text-xs font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                      {related.title}
                    </p>
                  </Link>
                ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}